import { clamp } from "./math";

export type RGB = { r: number; g: number; b: number };

/** True for `#RGB` or `#RRGGBB` and nothing else. Sync: the `venue_pages_themes_*_color_check` constraints. */
export function isHexColor(value: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/**
 * Converts hex color to RGB values
 */
function hexToRgb(hex: string): RGB | null {
  const cleanHex = hex.replace("#", "");

  if (cleanHex.length === 3) {
    return {
      r: parseInt(cleanHex[0] + cleanHex[0], 16),
      g: parseInt(cleanHex[1] + cleanHex[1], 16),
      b: parseInt(cleanHex[2] + cleanHex[2], 16),
    };
  }

  if (cleanHex.length === 6) {
    return {
      r: parseInt(cleanHex.substring(0, 2), 16),
      g: parseInt(cleanHex.substring(2, 4), 16),
      b: parseInt(cleanHex.substring(4, 6), 16),
    };
  }

  return null;
}

/**
 * Parses RGB string to extract color values
 */
function parseRgb(rgbString: string): RGB | null {
  const match = rgbString.match(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/);
  if (!match) return null;

  return {
    r: parseInt(match[1]),
    g: parseInt(match[2]),
    b: parseInt(match[3]),
  };
}

/**
 * Lightens a color by blending it with white
 * @param color - RGB color string (e.g., "rgb(255 94 38)") or hex color (e.g., "#ff5e26")
 * @param factor - Lightening factor between 0 and 1 (0.8 = 80% lighter)
 * @returns Lightened color string in same format as input
 */
export function lightenColor(color: string, factor: number = 0.8): string {
  let rgb: RGB | null = null;
  const isHex = color.startsWith("#") || /^[0-9a-fA-F]{3,6}$/.test(color);

  if (isHex) {
    rgb = hexToRgb(color);
  } else {
    rgb = parseRgb(color);
  }

  if (!rgb) {
    return color; // Return original if parsing fails
  }

  // Blend with white using the factor
  const r = Math.round(rgb.r + (255 - rgb.r) * factor);
  const g = Math.round(rgb.g + (255 - rgb.g) * factor);
  const b = Math.round(rgb.b + (255 - rgb.b) * factor);

  // Return in same format as input
  if (isHex) {
    const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
    return color.startsWith("#") ? `#${hex}` : hex;
  } else {
    return `rgb(${r} ${g} ${b})`;
  }
}

/**
 * Adds opacity to a color
 * @param color - RGB color string (e.g., "rgb(255 94 38)") or hex color (e.g., "#ff5e26")
 * @param opacity - Opacity value between 0 and 1 (0 = transparent, 1 = opaque)
 * @returns Color string with opacity applied (rgba format)
 */
export function withOpacity(color: string, opacity: number = 1): string {
  let rgb: RGB | null = null;
  const isHex = color.startsWith("#") || /^[0-9a-fA-F]{3,6}$/.test(color);

  if (isHex) {
    rgb = hexToRgb(color);
  } else {
    rgb = parseRgb(color);
  }

  if (!rgb) {
    return color; // Return original if parsing fails
  }

  // Clamp opacity between 0 and 1
  const clampedOpacity = clamp(opacity, 0, 1);

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clampedOpacity})`;
}

/**
 * Serialises an RGB colour to a CSS `rgb(r,g,b)` string (channels rounded).
 */
export function rgbToCssString(c: RGB): string {
  return `rgb(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)})`;
}

/**
 * Linearly interpolates between two RGB colours.
 * @param t - Blend factor 0..1 (0 = `a`, 1 = `b`)
 */
export function mixRgb(a: RGB, b: RGB, t: number): RGB {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

/**
 * Converts an RGB colour (0..255 channels) to HSL.
 * @returns `[h, s, l]` each in the 0..1 range
 */
export function rgbToHsl(
  r: number,
  g: number,
  b: number
): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (mx + mn) / 2;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    switch (mx) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h, s, l];
}

/**
 * Converts an HSL colour (0..1 channels) to RGB (0..255 channels).
 */
export function hslToRgb(h: number, s: number, l: number): RGB {
  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const f = (t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    r = f(h + 1 / 3);
    g = f(h);
    b = f(h - 1 / 3);
  }
  return { r: r * 255, g: g * 255, b: b * 255 };
}
